/**
 * Boli Live - Bid History Modal
 * 
 * Displays a detailed list of all bids for an auction.
 * eBay-style table layout with masked usernames.
 */

import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Trophy } from 'lucide-react';
import type { Bid } from '@boli/types';
import { formatPKR } from '../lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface BidHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    bids: Bid[];
    auctionTitle: string;
}

export function BidHistoryModal({ isOpen, onClose, bids, auctionTitle }: BidHistoryModalProps) {
    // Sort bids by amount (descending)
    const sortedBids = [...bids].sort((a, b) => b.amount - a.amount);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 z-[100] backdrop-blur-sm"
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-background rounded-xl shadow-2xl z-[101] overflow-hidden flex flex-col max-h-[80vh]"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-border">
                            <div>
                                <h2 className="text-lg font-bold text-foreground">Bid History</h2>
                                <p className="text-xs text-muted-foreground truncate max-w-[200px]">{auctionTitle}</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Stats */}
                        <div className="bg-muted/50 p-4 grid grid-cols-3 gap-4 text-center border-b border-border">
                            <div>
                                <div className="text-2xl font-bold text-foreground">{bids.length}</div>
                                <div className="text-xs text-muted-foreground">Total Bids</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-foreground">{new Set(bids.map(b => b.bidderId)).size}</div>
                                <div className="text-xs text-muted-foreground">Bidders</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-foreground overflow-hidden text-ellipsis px-1">
                                    {sortedBids[0] ? formatPKR(sortedBids[0].amount) : '-'}
                                </div>
                                <div className="text-xs text-muted-foreground">High Bid</div>
                            </div>
                        </div>

                        {/* List */}
                        <div className="flex-1 overflow-y-auto min-h-[300px]">
                            {bids.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full py-12 text-center text-muted-foreground">
                                    <Clock className="w-12 h-12 mb-3 opacity-20" />
                                    <p>No bids yet.</p>
                                    <p className="text-sm">Be the first to bid!</p>
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/30 text-muted-foreground font-medium text-xs uppercase sticky top-0 backdrop-blur-md">
                                        <tr>
                                            <th className="px-4 py-3 text-left">Bidder</th>
                                            <th className="px-4 py-3 text-right">Amount</th>
                                            <th className="px-4 py-3 text-right">Time</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {sortedBids.map((bid, index) => (
                                            <tr
                                                key={bid.id}
                                                className={index === 0 ? "bg-primary/5" : "hover:bg-muted/30"}
                                            >
                                                <td className="px-4 py-3 text-foreground font-medium flex items-center gap-2">
                                                    {index === 0 && <Trophy className="w-3 h-3 text-amber-500 fill-current" />}
                                                    {/* Masked User ID */}
                                                    {bid.bidderId.substring(0, 2)}***{bid.bidderId.slice(-2)}
                                                </td>
                                                <td className="px-4 py-3 text-right font-semibold">
                                                    {formatPKR(bid.amount)}
                                                </td>
                                                <td className="px-4 py-3 text-right text-muted-foreground text-xs whitespace-nowrap">
                                                    {formatDistanceToNow(bid.serverReceivedAt || Date.now(), { addSuffix: true })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
